<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import BusinessDay from './BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from '../helpers/businessHour';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const DEFAULT_TIMEZONE = {
  label: 'Brasilia (GMT-03:00)',
  value: 'America/Sao_Paulo',
};

export default {
  components: {
    SettingsToggleSection,
    SettingsFieldSection,
    BusinessDay,
    NextButton,
    ComboBox,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isBusinessHoursEnabled: false,
      unavailableMessage: '',
      timeZone: DEFAULT_TIMEZONE,
      timeSlots: [...defaultTimeSlot],
      sla_policy: {
        is_active: false,
        first_response_time_threshold: null,
        next_response_time_threshold: null,
        resolution_time_threshold: null,
        only_during_business_hours: false,
      },
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    dayNames() {
      return {
        0: this.$t('DAYS_OF_WEEK.SUNDAY'),
        1: this.$t('DAYS_OF_WEEK.MONDAY'),
        2: this.$t('DAYS_OF_WEEK.TUESDAY'),
        3: this.$t('DAYS_OF_WEEK.WEDNESDAY'),
        4: this.$t('DAYS_OF_WEEK.THURSDAY'),
        5: this.$t('DAYS_OF_WEEK.FRIDAY'),
        6: this.$t('DAYS_OF_WEEK.SATURDAY'),
      };
    },
    hasError() {
      if (!this.isBusinessHoursEnabled) return false;
      return this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
    },
    timeZones() {
      return [...timeZoneOptions()];
    },
    timeZoneValue: {
      get() {
        return this.timeZone.value;
      },
      set(value) {
        const match = this.timeZones.find(tz => tz.value === value);
        if (match) this.timeZone = match;
      },
    },
    isRichEditorEnabled() {
      if (this.isATwitterInbox || this.isAFacebookInbox) return false;
      return true;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        working_hours_enabled: isEnabled = false,
        out_of_office_message: unavailableMessage,
        working_hours: timeSlots = [],
        timezone: timeZone,
      } = this.inbox;
      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;
      this.isBusinessHoursEnabled = isEnabled;
      this.unavailableMessage = unavailableMessage || '';
      this.timeSlots = slots;
      this.timeZone =
        this.timeZones.find(item => timeZone === item.value) ||
        DEFAULT_TIMEZONE;

      if (this.inbox.sla_policy) {
        this.sla_policy = { ...this.inbox.sla_policy };
      }
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          working_hours_enabled: this.isBusinessHoursEnabled,
          out_of_office_message: this.unavailableMessage,
          working_hours: timeSlotTransform(this.timeSlots),
          timezone: this.timeZone.value,
          channel: {},
          sla_policy: {
            ...this.sla_policy,
            is_active: this.isBusinessHoursEnabled,
            first_response_time_threshold:
              Number(this.sla_policy.first_response_time_threshold) || null,
            next_response_time_threshold:
              Number(this.sla_policy.next_response_time_threshold) || null,
            resolution_time_threshold:
              Number(this.sla_policy.resolution_time_threshold) || null,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="w-full">
    <SettingsToggleSection
      v-model="isBusinessHoursEnabled"
      :header="$t('WEEKLY_AVAILABILITY.ENABLE_SLA')"
      :description="$t('WEEKLY_AVAILABILITY.ENABLE_SLA_DESCRIPTION')"
    />

    <form class="flex flex-col" @submit.prevent="updateInbox">
      <div v-if="isBusinessHoursEnabled" class="mt-8">
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ $t('WEEKLY_AVAILABILITY.SLA_CONFIGURATION') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ $t('WEEKLY_AVAILABILITY.SLA_DESCRIPTION') }}
        </p>

        <div class="mt-4 grid gap-4 grid-cols-1 md:grid-cols-3">
          <label class="flex flex-col">
            <span class="text-sm font-medium mb-1">{{
              $t('WEEKLY_AVAILABILITY.FIRST_RESPONSE')
            }}</span>
            <input
              v-model="sla_policy.first_response_time_threshold"
              type="number"
              class="border-n-weak rounded"
              :placeholder="$t('WEEKLY_AVAILABILITY.MINUTES_15')"
              required
              min="0"
              step="0.5"
            />
          </label>
          <label class="flex flex-col">
            <span class="text-sm font-medium mb-1">{{
              $t('WEEKLY_AVAILABILITY.NEXT_RESPONSE')
            }}</span>
            <input
              v-model="sla_policy.next_response_time_threshold"
              type="number"
              class="border-n-weak rounded"
              :placeholder="$t('WEEKLY_AVAILABILITY.MINUTES_30')"
              required
              min="0"
              step="0.5"
            />
          </label>
          <label class="flex flex-col">
            <span class="text-sm font-medium mb-1">{{
              $t('WEEKLY_AVAILABILITY.RESOLUTION')
            }}</span>
            <input
              v-model="sla_policy.resolution_time_threshold"
              type="number"
              class="border-n-weak rounded"
              :placeholder="$t('WEEKLY_AVAILABILITY.MINUTES_60')"
              required
              min="0"
              step="0.5"
            />
          </label>
        </div>

        <div class="mt-4">
          <SettingsToggleSection
            v-model="sla_policy.only_during_business_hours"
            :header="$t('WEEKLY_AVAILABILITY.SLA_BUSINESS_HOURS_ONLY')"
            :description="
              $t('WEEKLY_AVAILABILITY.SLA_BUSINESS_HOURS_DESCRIPTION')
            "
            class="!px-0 !py-2"
          />
        </div>
      </div>

      <template
        v-if="isBusinessHoursEnabled && sla_policy.only_during_business_hours"
      >
        <div class="flex items-center my-8 py-1">
          <div class="flex-1 h-px bg-n-weak" />
          <span class="text-body-main text-n-slate-11 px-2">
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE') }}
          </span>
          <div class="flex-1 h-px bg-n-weak" />
        </div>

        <SettingsFieldSection
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL')"
        >
          <ComboBox
            v-model="timeZoneValue"
            :options="timeZones"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            class="[&>div>button]:!bg-n-alpha-black2"
          />
        </SettingsFieldSection>

        <div class="mt-2">
          <div class="w-full">
            <table
              class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
            >
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.DAY') }}
                  </th>
                  <th
                    class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.AVAILABILITY') }}
                  </th>
                  <th
                    class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <BusinessDay
                  v-for="timeSlot in timeSlots"
                  :key="timeSlot.day"
                  :day-name="dayNames[timeSlot.day]"
                  :time-slot="timeSlot"
                  @update="data => onSlotUpdate(timeSlot.day, data)"
                />
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <div class="w-full flex justify-end items-center py-4 mt-2">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.UPDATE')"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
:deep(.message-editor) {
  @apply border-0;
}

textarea {
  @apply min-h-[4rem] mt-1.5;
}
</style>
